// ============================================================================
// STAY CHARGES — what the departing rooms owe beyond their nights (PMS slice 7c-2)
// ============================================================================
// Computed by the server from the property's Stay Policy (Configuration) and the
// check-in / check-out times on the Property Master: early check-in, late
// check-out, overstay, retention of unused nights. The property's entitlement is
// what is charged. Only a user with the matching permission may charge less
// (relax-late-checkout for early / late, waive-retention for retention), with a
// reason — kept on the voucher and the waiver register. An overstay night is
// never reduced here.

import React from 'react';
import { formatCurrency } from 'utils/currencyFormat';
import type { StayChargeLine, StayChargePreview } from 'api/types/departure.types';

export interface StayChargeEdit {
  amount: string;
  reason: string;
}

interface Props {
  preview: StayChargePreview;
  edits: Record<string, StayChargeEdit>;
  onChange: (key: string, edit: StayChargeEdit) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}

const KIND_LABEL: Record<string, string> = {
  EARLY_CHECKIN: 'Early check-in',
  LATE_CHECKOUT: 'Late check-out',
  OVERSTAY: 'Overstay night',
  RETENTION: 'Retention',
};

/** The amount the line will be charged (the edit, else the entitlement). */
export const chargedAmount = (line: StayChargeLine, edits: Record<string, StayChargeEdit>): number => {
  const e = edits[line.key];
  if (!e || e.amount.trim() === '') return line.proposed;
  const n = Number(e.amount);
  return Number.isFinite(n) ? n : line.proposed;
};

/** A late check-out on a retained morning is paid by that night. */
export const isCovered = (line: StayChargeLine, lines: StayChargeLine[], edits: Record<string, StayChargeEdit>): boolean => {
  if (!line.dueUnlessCharged) return false;
  const night = lines.find((l) => l.key === line.dueUnlessCharged);
  return !!night && chargedAmount(night, edits) > 0;
};

const StayChargesPanel: React.FC<Props> = ({ preview, edits, onChange, errors, disabled }) => {
  const lines = preview.lines.filter((l) => !l.alreadyDone);
  if (lines.length === 0) return null;
  const mayRelax = (l: StayChargeLine) =>
    l.relaxAction === 'relax-late-checkout' ? preview.canRelaxEarlyLate
      : l.relaxAction === 'waive-retention' ? preview.canWaiveRetention : false;
  const total = lines.filter((l) => !isCovered(l, lines, edits)).reduce((s, l) => s + chargedAmount(l, edits), 0);

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, marginBottom: '0.75rem' }}>
      <div style={{ padding: '0.45rem 0.75rem', background: 'var(--bg-secondary)', fontSize: '0.8125rem', display: 'flex', gap: '0.5rem' }}>
        <b>Stay charges</b>
        <span style={{ color: 'var(--text-muted)' }}>
          check-in {preview.checkInTime.slice(0, 5)} · check-out {preview.checkOutTime.slice(0, 5)}
          {lines.some((l) => l.kind === 'RETENTION') && ` · unused nights: ${preview.retentionMode === 'FULL' ? 'retained' : preview.retentionMode === 'ASK' ? 'confirm' : 'released'} (${preview.retentionSource})`}
        </span>
        <b style={{ marginLeft: 'auto' }}>{formatCurrency(total)}</b>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <tbody>
          {lines.map((l) => {
            const edit = edits[l.key] ?? { amount: '', reason: '' };
            const covered = isCovered(l, lines, edits);
            const reduced = chargedAmount(l, edits) < l.entitled;
            const editable = mayRelax(l) && !covered;
            return (
              <tr key={l.key} style={{ borderTop: '1px solid var(--border-color)', opacity: covered ? 0.55 : 1 }}>
                <td style={{ padding: '0.35rem 0.6rem' }}>
                  <div><b>{KIND_LABEL[l.kind] ?? l.kind}</b> — {l.description.split(' — ').slice(1).join(' — ') || l.description}</div>
                  {covered && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Paid by the retained night — charged only if that night is waived.</div>}
                  {l.kind === 'OVERSTAY' && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Posted as a room night at the agreed rate; correct it by extending the stay or a credit note.</div>}
                  {l.requiresDecision && <div style={{ fontSize: '0.72rem', color: 'var(--warning-text, #92400e)' }}>The property asks: confirm or reduce.</div>}
                  {editable && reduced && (
                    <input type="text" className={`form-input${errors[l.key] ? ' form-field-error' : ''}`} style={{ marginTop: 4, height: '1.9rem', fontSize: '0.78rem' }}
                      placeholder="Reason for charging less (required, on the waiver register)" value={edit.reason} maxLength={200} disabled={disabled}
                      onChange={(e) => onChange(l.key, { ...edit, reason: e.target.value })} />
                  )}
                  {errors[l.key] && <div className="form-error-message">{errors[l.key]}</div>}
                </td>
                <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap', width: 150 }}>
                  {editable ? (
                    <input type="number" className="form-input" style={{ width: 120, height: '1.9rem', textAlign: 'right' }}
                      min={0} max={l.entitled} step={0.01} disabled={disabled}
                      value={edit.amount === '' ? String(l.proposed) : edit.amount}
                      onChange={(e) => onChange(l.key, { ...edit, amount: e.target.value })} />
                  ) : formatCurrency(covered ? 0 : l.proposed)}
                  {reduced && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>of {formatCurrency(l.entitled)}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StayChargesPanel;
